import { FC, useEffect, useState, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { FormikSelectSearchField } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";
import { getObjectsByQueryThunk } from "app/features";
import {
  selectQueriedObjectsState,
  selectDrawerSelectedAccountId
} from "app/data/selectors";

interface MileageObjectFilterProps {
  name: string;
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
}

const MileageObjectFilter: FC<MileageObjectFilterProps> = ({
  name,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  multiple = false
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["translation", "formFieldsTranslation"]);
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { objects, loading } = useAppSelector(selectQueriedObjectsState);

  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(
    async (searchValue: string) => {
      if (!accountId) return;

      setSearchTerm(searchValue);

      const whereConditions = [];

      if (searchValue && searchValue.length >= 2) {
        whereConditions.push({
          field: "name",
          value: searchValue,
          type: 0
        });
      }

      await dispatch(
        getObjectsByQueryThunk({
          accountId: accountId,
          params: {
            itemsPerPage: 50,
            page: 1,
            where: whereConditions
          }
        })
      );
    },
    [accountId, dispatch]
  );

  const handleDebouncedSearch = useCallback(
    (searchValue: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        handleSearch(searchValue);
      }, 500);
    },
    [handleSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialLoad && accountId && (!objects || objects.length === 0)) {
      setIsInitialLoad(false);
      handleSearch("");
    }
  }, [isInitialLoad, accountId, objects, handleSearch]);

  const options = objects || [];

  return (
    <FormikSelectSearchField
      name={name}
      fetchOnDebounce={handleDebouncedSearch}
      options={options}
      optionLabelKey="name"
      optionValueKey="uniqueId"
      loading={loading}
      multiple={multiple}
      searchable
      disabled={disabled}
      placeholder={
        placeholder || t("objects.placeholder", { ns: "formFieldsTranslation" })
      }
      label={label || t("objects.label", { ns: "formFieldsTranslation" })}
      fullWidth
      onChange={onChange}
      sx={{
        marginTop: "0 !important",
        "& .MuiAutocomplete-hasClearIcon.css-1x6bjyf-MuiAutocomplete-root .MuiOutlinedInput-root":
          {
            paddingRight: "9px"
          }
      }}
    />
  );
};

export default MileageObjectFilter;
